import { lazy, Suspense, type ComponentType, type ReactElement } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { Card, PageHeader } from '@zarya/ui';
import { RequireAuth } from './app/auth/RequireAuth';
import { LoginPage } from './app/auth/Login/LoginPage';
import { RequireRole } from './app/auth/RequireRole';
import { Shell } from './app/main/Shell/Shell';
import { ScreenErrorBoundary } from './app/main/Shell/ScreenErrorBoundary';
import { InfraPage } from './app/main/Infra/InfraPage';
import { TenantSettingsPage } from './app/main/TenantSettings/TenantSettingsPage';
import { OverviewPage } from './app/main/Overview';
import { ApprovalsPage } from './app/main/Approvals';
import { ContractsPage } from './app/main/Contracts';
import { DecisionsPage } from './app/main/Decisions';
import { ResourcesPage } from './app/main/Resources';
import { RuntimePage } from './app/main/Runtime';
import { AccessMapPage } from './app/main/AccessMap';
import { SimulatorPage } from './app/main/Simulator';
import { DemoConsolePage } from './app/main/DemoConsole';
import { ChatPage } from './app/main/Chat';
import { PoliciesPage } from './app/main/Policies';
import { AccessControlPage } from './app/main/AccessControl';
import { UsersPage } from './app/main/Users';
import { AuditPage } from './app/main/Audit';
import { IdentitiesPage } from './app/main/Identities';
import { CommandsPage } from './app/main/Commands';
import { ApprovalWorkflowPage } from './app/main/ApprovalWorkflow/ApprovalWorkflowPage';
import { EnvironmentsPage } from './app/main/Environments/EnvironmentsPage';
import { SiteAdmissionPage, SitesPage } from './app/main/Sites';
import { useCurrentUser, hasRole } from './app/auth/useCurrentUser';
import { t } from '@app/i18n';
import {
  ROUTES,
  screenTitle,
  ROUTE_CHAT,
  ROUTE_COMMANDS,
  ROUTE_STALE_CONVERSE,
  ROUTE_DEMO,
  ROUTE_DOCS,
  ROUTE_DOCSAPI,
  ROUTE_LOGIN,
  ROUTE_ACCESS_CONTROL,
  ROUTE_HUMAN_ACCESS,
  ROUTE_IDENTITY_ACCESS,
  ROUTE_STALE_POLICIES,
  IDENTITY_PARAM,
  type RouteConsole,
} from './app/navigation';
import { useTabTitle } from './app/tabTitle';
import s from './routes.module.css';

/**
 * Página de referência do design system — só em desenvolvimento (T011).
 * O ternário sobre `import.meta.env.DEV` deixa o Rollup eliminar o import
 * dinâmico no build de produção (nenhum chunk do preview é emitido).
 */
const ThemePreview = import.meta.env.DEV
  ? lazy(async () => {
      const mod = await import('./dev/ThemePreview');
      return { default: mod.ThemePreview };
    })
  : null;

/**
 * Documentação do produto em chunk próprio (16/09).
 *
 * Ela traz `react-markdown` + `remark-gfm`. Medido nos dois builds: o chunk principal cai
 * de 1 572,33 kB para 1 403,75 kB (comprimido, de 467,97 kB para 416,52 kB) e o chunk de
 * `/docs` fica com 168,35 kB (51,74 kB comprimidos). Juntos, entravam no chunk que TODA
 * sessão do console baixa, para servir uma rota que quase ninguém abre; separados, custam
 * zero a quem não visita `/docs`. É a mesma conta que
 * pôs o Scalar no CDN em `ApiReference`, resolvida aqui sem depender da rede: o chunk é
 * nosso, servido pelo mesmo nginx, e funciona em instalação sem saída para a internet.
 */
const DocsPlatformPage = lazy(async () => {
  const mod = await import('./app/docs/Platform');
  return { default: mod.DocsPlatformPage };
});

/**
 * Rotas do console (T058). O **shell** (T2) já é o definitivo — rail, topbar,
 * sessão e papéis; as telas T3–T23 entram cada uma na sua story e substituem o
 * `Placeholder` da sua rota.
 *
 * Convenção de drawer: painel aberto é `?drawer=<id>` **na mesma rota**, nunca
 * uma rota própria (`useDrawerParam` em `app/navigation.ts`).
 */
function DocsPlatform() {
  // A guarda de sessão da documentação mora DENTRO de `DocsPlatformPage` (01/10), e não num
  // `RequireAuth` aqui: sem sessão, a página pública abre e a fechada vai ao login, e só a
  // página sabe, pelo sumário público, qual é qual.
  return (
    <Suspense fallback={<div className={s.carregando}>{t('docs.loading')}</div>}>
      <DocsPlatformPage />
    </Suspense>
  );
}

function Placeholder({ rota }: { rota: RouteConsole }) {
  return (
    <>
      <PageHeader
        title={screenTitle(rota)}
        subtitle={t('shell.placeholder.subtitle', { screen: rota.tela })}
        icon={rota.icon}
      />
      <Card title={t('shell.placeholder.cardTitle')}>
        <p>{t('shell.placeholder.body', { screen: rota.tela })}</p>
      </Card>
    </>
  );
}

/**
 * Telas T* implementadas, por rota. O que não está aqui ainda é `Placeholder`.
 *
 * **Componentes, não elementos.** Isto era `Record<string, ReactElement>` com
 * `'/': <OverviewPage />` — o elemento nascia no import, com a MESMA referência em todo
 * render, e o React pula a re-renderização de um filho cujo elemento não mudou. Efeito:
 * trocar o idioma no seletor repintava o shell e deixava a tela inteira no idioma
 * anterior (T3, T15, T18, T21 e o hub — as que não têm polling próprio para
 * «acordar»). O elemento agora nasce no render de `AppRoutes` (qa-i18n, 09/09).
 */
const TELAS: Record<string, ComponentType> = {
  '/mapa': AccessMapPage,
  '/runtime': RuntimePage,
  // COMPONENTE, nunca elemento (`<SitesPage />`): o elemento nasceria no import,
  // com a mesma referencia em todo render, e trocar o idioma no seletor deixaria a
  // tela no idioma anterior — o achado do qa-i18n descrito no doc de `TELAS`.
  '/sites': SitesPage,
  '/resources': ResourcesPage,
  '/simulador': SimulatorPage,
  [ROUTE_ACCESS_CONTROL]: AccessControlRedirect,
  [ROUTE_HUMAN_ACCESS]: PoliciesPage,
  [ROUTE_IDENTITY_ACCESS]: AccessControlPage,
  '/decisoes': DecisionsPage,
  '/contratos': ContractsPage,
  '/': OverviewPage,
  '/aprovacoes': ApprovalsPage,
  '/usuarios': UsersPage,
  '/auditoria': AuditPage,
  '/identidades': IdentitiesPage,
  '/infra': InfraPage,
  '/tenant': TenantSettingsPage,
  '/settings/environments': EnvironmentsPage,
  '/approval-workflow': ApprovalWorkflowPage,
};

function VerificandoSession() {
  return <div className={s.carregando}>{t('common.session.checking')}</div>;
}

/**
 * Redirect de endereço antigo **com a query string** (03/09, Human × Identity
 * Access).
 *
 * `<Navigate to="/x" />` com destino fixo descarta o `?…`, e é justamente o que
 * não pode acontecer aqui: quem escreve `/politicas` não é só o usuário que
 * guardou o link — é o servidor, em três lugares (busca global, auditoria e o
 * mapa de acesso escrevem `/politicas?drawer=<id>`). Sem a query o link abriria a
 * lista em vez da regra que a linha aponta.
 */
function RedirectKeepingQuery({ to }: { to: string }) {
  const { search, hash } = useLocation();
  return <Navigate to={`${to}${search}${hash}`} replace />;
}

/**
 * `/access-control` não tem tela desde 12/10/2026: o hub saiu, e no menu o grupo «Controle de acesso» é só um
 * rótulo. O endereço continua valendo para quem o guardou, e o `?identidade=` decide para onde ele vai.
 *
 * Com o parâmetro, é o link antigo de T14 («abrir o access control desta identidade»), e vai para Identity Access
 * com a query inteira. O critério é a **presença** do parâmetro, não o valor: `?identidade=` vazio já é um link de
 * T14 (um id que não veio), e a tela da identidade sabe pedir a escolha. Sem o parâmetro, vai para Human Access
 * (T11), o primeiro item do grupo, e não para a Visão geral do `path="*"`.
 */
function AccessControlRedirect() {
  const { search, hash } = useLocation();
  const to = new URLSearchParams(search).has(IDENTITY_PARAM)
    ? ROUTE_IDENTITY_ACCESS
    : ROUTE_HUMAN_ACCESS;
  return <Navigate to={`${to}${search}${hash}`} replace />;
}

/**
 * Guarda das rotas ocultas — `/comandos` (T24) e `/console-de-demo` (29/08):
 * quem não é admin vai para a Visão geral — **o mesmo destino de uma URL que não
 * existe** (`path="*"` abaixo), e não a tela de recusa do `RequireRole`. É de
 * propósito: nenhuma das duas anuncia a própria existência a quem não pode
 * usá-la.
 *
 * Papel desconhecido (sessão em modo degradado) passa, como em todas as guardas
 * do console — quem recusa de verdade é o backend, que exige admin em `/debug/*`.
 */
function OnlyAdmin({ children }: { children: ReactElement }) {
  const { papel } = useCurrentUser();
  return hasRole(papel, ['admin']) ? children : <Navigate to="/" replace />;
}

/**
 * Guarda de `/chat` (04/09): admin **e approver** — os mesmos papéis das rotas
 * `agentes` que a tela chama. Mesmo desenho de `OnlyAdmin`: quem não pode vai
 * para a Visão geral, sem a página anunciar a própria existência.
 */
function OnlyOperator({ children }: { children: ReactElement }) {
  const { papel } = useCurrentUser();
  return hasRole(papel, ['admin', 'approver']) ? children : <Navigate to="/" replace />;
}

export function AppRoutes() {
  // Título da aba por rota (30/08): `360 AI - {tela}`. Fica aqui, e não no
  // `Shell`, porque `/login` e `/console-de-demo` vivem fora dele — e o próprio
  // hook deixa `/docs/api` em paz, que define o seu.
  useTabTitle();

  return (
    <Routes>
      {ThemePreview ? (
        <Route
          path="/dev/tema"
          element={
            <Suspense fallback={<p>{t('common.loading')}</p>}>
              <ThemePreview />
            </Suspense>
          }
        />
      ) : null}

      <Route path={ROUTE_LOGIN} element={<LoginPage />} />

      {/* **A documentação inteira, numa casca só** (16/09, fase 3). Fora da rota-mãe do
          `Shell` — sem rail e sem topbar, como `/console-de-demo` — e, desde 30/09, **com
          sessão**: nenhuma URL da documentação abre sem login (pedido do mrolim), salvo a
          página marcada `access: public` quando a instalação está em `porPagina` (01/10).
          A guarda está dentro de `DocsPlatformPage`, então as cinco
          rotas abaixo herdam a mesma, e quem chega sem sessão vai a `/login?retorno=` e
          volta à página depois de entrar. O conteúdo é Markdown servido estático de
          `/docs/platform/`, e a referência da API é o recorte público em
          `/docs/api-publica.yaml`; o nginx exige sessão nesses arquivos também
          (`auth_request` em `nginx.conf.template`), senão a guarda da tela seria só
          aparência — o Markdown sairia direto pelo caminho do arquivo.

          As cinco rotas vão ao MESMO componente, que lê o endereço e decide. Deixar a
          decisão num lugar só é o que permite afirmar a tabela do §4 do protótipo linha a
          linha, em vez de espalhá-la por cinco elementos.

          A ordem entre elas é do ranking do router, e não da escrita: segmento estático
          ganha de dinâmico, então `/docs/api` ganha de `/docs/:slug`; e o splat perde
          para todas, então ele só vê o que nenhuma atendeu. */}
      <Route path={ROUTE_DOCS} element={<DocsPlatform />} />
      <Route path={ROUTE_DOCSAPI} element={<DocsPlatform />} />
      <Route path={`${ROUTE_DOCS}/:slug`} element={<DocsPlatform />} />

      {/* O endereço da v1, `/docs/<trilha>/<pagina>`: redireciona para `/docs/<slug>`,
          porque o slug é a identidade estável e a trilha não é. Um link guardado por
          alguém, ou escrito num e-mail, continua a chegar à página. */}
      <Route path={`${ROUTE_DOCS}/:track/:slug`} element={<DocsPlatform />} />

      {/* **Qualquer outro endereço sob `/docs` fica na documentação** (16/09). Sem esta
          rota, `/docs/platform/pt-BR/entenda` — um diretório do conteúdo servido, que o
          nginx não resolve e devolve como `index.html` — não casa com rota nenhuma,
          cai no `path="*"` e vai para `/`, e o leitor perde a página que abriu. */}
      <Route path={`${ROUTE_DOCS}/*`} element={<DocsPlatform />} />

      {/* Console de demonstração (29/08): **fora** da rota-mãe do `Shell`, e é
          isso que tira dela o rail, a topbar, os contadores e o sino. A guarda
          de sessão vem junto porque ela deixou de herdar a do shell; a de papel
          é a mesma de `/comandos` (admin, sem anunciar a própria existência a
          quem não pode usá-la). Dentro do shell a moldura do produto voltaria,
          que é justamente o que o pedido tirou. */}
      <Route
        path={ROUTE_DEMO}
        element={
          <RequireAuth fallback={<VerificandoSession />}>
            <OnlyAdmin>
              <DemoConsolePage />
            </OnlyAdmin>
          </RequireAuth>
        }
      />
      {/* O endereço antigo de T25. Rota estática ganha do `path="*"` no ranking
          do router, então o link guardado chega à página nova em vez de cair na
          Visão geral. */}
      <Route path={ROUTE_STALE_CONVERSE} element={<Navigate to={ROUTE_DEMO} replace />} />

      {/* Chat «Ask» (04/09): fora do `Shell` como o console de demo — moldura
          própria, sem rail nem topbar —, mas seguindo o tema do produto. Sessão
          e papel (admin, approver) na própria rota, porque ela não herda os do
          shell. */}
      <Route
        path={ROUTE_CHAT}
        element={
          <RequireAuth fallback={<VerificandoSession />}>
            <OnlyOperator>
              <ChatPage />
            </OnlyOperator>
          </RequireAuth>
        }
      />

      <Route
        element={
          <RequireAuth fallback={<VerificandoSession />}>
            <Shell />
          </RequireAuth>
        }
      >
        {ROUTES.map((rota) => {
          // Telas já implementadas substituem o `Placeholder` da sua rota.
          const Screen = TELAS[rota.href];
          const screen = Screen ? <Screen /> : <Placeholder rota={rota} />;
          const withRole = rota.papeis ? (
            <RequireRole papeis={rota.papeis}>{screen}</RequireRole>
          ) : (
            screen
          );
          return (
            <Route
              key={rota.href}
              path={rota.href}
              // O limite fica **dentro** do shell e por rota: quando uma leitura
              // acessória fora do contrato derruba o render, o rail e a topbar
              // ficam de pé e o card diz qual rota quebrou e com que request-id.
              element={<ScreenErrorBoundary rota={rota}>{withRole}</ScreenErrorBoundary>}
            />
          );
        })}
        {/* Endereços antigos de T11 e T14 (03/09). Rota estática ganha do
            `path="*"` no ranking do router, então o link guardado — e o que o
            servidor escreve — chega à tela nova em vez de cair na Visão geral.
            `/access-control` é o rótulo do grupo no menu e redireciona
            (`AccessControlRedirect`). */}
        <Route
          path={ROUTE_STALE_POLICIES}
          element={<RedirectKeepingQuery to={ROUTE_HUMAN_ACCESS} />}
        />
        {/* aim-xsk.8 — a admissão no cluster de um Site. Página filha de «Sites», fora de `ROUTES` (não é item
            do rail); só o administrador vê e edita (protótipo), e quem não é vai para a Visão geral, como as
            outras rotas de admin daqui. */}
        <Route
          path="/sites/:slug/admissao"
          element={
            <OnlyAdmin>
              <SiteAdmissionPage />
            </OnlyAdmin>
          }
        />
        {/* Rota oculta: fora de `ROTAS`, logo fora do rail e da busca (T24). */}
        <Route
          path={ROUTE_COMMANDS}
          element={
            <OnlyAdmin>
              <CommandsPage />
            </OnlyAdmin>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}
